import { useCallback, useEffect, useState } from "react";
import { auth } from "../firebase";

const STORAGE_KEY = "lex:social-auth-debug:v1";
const EVENT_NAME = "lex-social-auth-debug";
const MAX_ROWS = 20;

function canUseBrowserStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return false;
    const key = "__lex_social_debug_test__";
    window.sessionStorage.setItem(key, "1");
    window.sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function getSocialAuthDebugRows() {
  if (!canUseBrowserStorage()) return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearSocialAuthDebugRows() {
  if (!canUseBrowserStorage()) return;
  window.sessionStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function buildSocialAuthDebugSnapshot(extra = {}) {
  let localStorageStatus = "unknown";
  let sessionStorageStatus = "unknown";
  try {
    const key = "__lex_social_debug_local__";
    window.localStorage.setItem(key, "1");
    window.localStorage.removeItem(key);
    localStorageStatus = "ok";
  } catch (err) {
    localStorageStatus = err?.name || err?.message || "blocked";
  }
  try {
    const key = "__lex_social_debug_session__";
    window.sessionStorage.setItem(key, "1");
    window.sessionStorage.removeItem(key);
    sessionStorageStatus = "ok";
  } catch (err) {
    sessionStorageStatus = err?.name || err?.message || "blocked";
  }

  return {
    time: new Date().toISOString(),
    href: typeof window !== "undefined" ? window.location.href : "",
    origin: typeof window !== "undefined" ? window.location.origin : "",
    authDomain: auth?.config?.authDomain || "",
    currentUserUid: auth?.currentUser?.uid || null,
    currentUserEmail: auth?.currentUser?.email || null,
    localStorage: localStorageStatus,
    sessionStorage: sessionStorageStatus,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    ...extra,
  };
}

export function addSocialAuthDebug(label, extra = {}) {
  const snapshot = buildSocialAuthDebugSnapshot(extra);
  const row = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    label,
    snapshot,
  };
  const rows = [row, ...getSocialAuthDebugRows()].slice(0, MAX_ROWS);
  if (canUseBrowserStorage()) {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    window.dispatchEvent(new Event(EVENT_NAME));
  }
  console.log("[social-auth-debug]", label, snapshot);
  return row;
}

export function useSocialAuthDebugRows() {
  const [rows, setRows] = useState(() => getSocialAuthDebugRows());

  useEffect(() => {
    const sync = () => setRows(getSocialAuthDebugRows());
    window.addEventListener(EVENT_NAME, sync);
    window.addEventListener("storage", sync);
    sync();
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addRow = useCallback((label, extra = {}) => {
    addSocialAuthDebug(label, extra);
  }, []);

  const clearRows = useCallback(() => {
    clearSocialAuthDebugRows();
  }, []);

  return { rows, addRow, clearRows };
}
